import React from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
    FiGrid,
    FiMessageSquare,
    FiEdit3,
    FiImage,
    FiLogOut,
    FiUser,
    FiExternalLink
} from 'react-icons/fi';

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const menuItems = [
        { label: 'Dashboard', path: '/admin', icon: FiGrid },
        { label: 'Messages', path: '/admin/messages', icon: FiMessageSquare },
        { label: 'Blogs', path: '/admin/blogs', icon: FiEdit3 },
        { label: 'Projects', path: '/admin/projects', icon: FiImage },
    ];

    return (
        <div className="flex h-screen bg-gray-50 scroll-smooth">
            {/* Sidebar */}
            <aside className="w-64 bg-slate-900 text-white flex flex-col shrink-0 shadow-xl">
                <div className="p-6 flex items-center gap-3 border-b border-slate-800">
                    <div className="w-8 h-8 bg-[#2E63E8] rounded flex items-center justify-center font-bold text-black text-lg">B</div>
                    <span className="font-bold text-lg tracking-tight uppercase">Bliv Design Studio</span>
                </div>

                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    {menuItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group ${isActive
                                    ? 'bg-[#2E63E8] text-black font-semibold'
                                    : 'hover:bg-slate-800 text-slate-400 hover:text-white'
                                    }`}
                            >
                                <item.icon className={isActive ? 'text-black' : 'group-hover:text-white'} />
                                {item.label}
                            </Link>
                        );
                    })}
                </nav>

                <div className="p-4 border-t border-slate-800">
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-3 text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
                    >
                        <FiLogOut />
                        Logout
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Header */}
                <header className="h-16 bg-white border-b flex items-center justify-between px-8 shadow-sm shrink-0">
                    <div className="flex items-center gap-2 text-slate-600">
                        <h2 className="font-semibold text-lg">
                            {menuItems.find(m => m.path === location.pathname)?.label || 'Admin Panel'}
                        </h2>
                    </div>

                    <div className="flex items-center gap-6">
                        <a
                            href="/"
                            target="_blank"
                            className="text-sm text-slate-500 hover:text-[#2E63E8] flex items-center gap-1.5 transition-colors"
                        >
                            View Site <FiExternalLink size={14} />
                        </a>
                        <div className="flex items-center gap-3 pl-6 border-l">
                            <div className="text-right">
                                <p className="text-sm font-bold text-slate-900 leading-none">Administrator</p>
                                <p className="text-xs text-slate-500 mt-1">{user?.email}</p>
                            </div>
                            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 border">
                                <FiUser />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Area */}
                <main className="flex-1 overflow-y-auto p-8 relative">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
